import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import axios from 'axios';
import { useOutlookStatus } from './useOutlook';
import { extractAddress, splitAddressList } from '../lib/emailAddressList';
import { withoutSignatureBlocks } from '../lib/editorSafeHtml';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CRM inbox emails (sequence emails, tracked emails).
 * Polls every 60s for new items.
 * @param {boolean} enabled - Set to false to disable polling (e.g. when
 *   viewing a unified inbox instead of the personal inbox).
 */
export function useInboxEmails(enabled = true) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['inbox', 'emails'],
        queryFn: () => apiFetch('/inbox/emails', getAuthHeader()),
        enabled,
        staleTime: 60 * 1000,
        refetchInterval: 60 * 1000,
        select: (data) => ({
            emails: data?.emails || [],
            counts: data?.counts || { primary: 0, emails: 0, calls: 0, messages: 0, tasks: 0, reminders: 0 },
        }),
    });
}

/**
 * All CRM communications (calls, messages, tasks, reminders).
 * Polls every 60s.
 * @param {boolean} enabled - Set to false to disable polling (e.g. when
 *   viewing a unified inbox instead of the personal inbox).
 */
export function useCommunications(enabled = true) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['communications'],
        queryFn: async () => {
            const data = await apiFetch('/communications', getAuthHeader());
            return Array.isArray(data) ? data : (data?.data || []);
        },
        enabled,
        staleTime: 60 * 1000,
        refetchInterval: 60 * 1000,
    });
}

/**
 * Gmail OAuth connection status.
 */
export function useGmailStatus() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();

    // F08: Listen for connect/disconnect events from SettingsIntegrationsPage
    // so React Query consumers (Inbox) see the update immediately instead of
    // serving stale data for up to 5 minutes. Mirrors the Outlook pattern in
    // useOutlook.js.
    useEffect(() => {
        const invalidate = () => {
            queryClient.invalidateQueries({ queryKey: ['gmail', 'status'] });
            queryClient.invalidateQueries({ queryKey: ['gmail', 'emails'] });
        };
        window.addEventListener('gmail-connected', invalidate);
        window.addEventListener('gmail-disconnected', invalidate);
        return () => {
            window.removeEventListener('gmail-connected', invalidate);
            window.removeEventListener('gmail-disconnected', invalidate);
        };
    }, [queryClient]);

    return useQuery({
        queryKey: ['gmail', 'status'],
        queryFn: async () => {
            try {
                const data = await apiFetch('/oauth/gmail/status', getAuthHeader());
                return {
                    connected: data.connected,
                    needs_reconnect: data.needs_reconnect || false,
                    // #484 — one field name across both providers, so
                    // usePersonalSenders never branches on provider. Gmail has
                    // no calendar-scope overloading, so the backend emits the
                    // same value here; the `??` fallback covers a rolling
                    // deploy where the backend is still the older image.
                    mail_needs_reconnect:
                        data.mail_needs_reconnect ?? data.needs_reconnect ?? false,
                    email: data.gmail_email || ''
                };
            } catch (err) {
                // Only a definitive "no mailbox" answer may be reported as
                // disconnected. Swallowing every error made a 500 or a
                // timeout indistinguishable from "not connected", which
                // silently routed a real reply into a CRM-log-only row and
                // still reported success (#487). Anything else propagates so
                // `data` stays undefined and the send route resolves as
                // 'unknown'. Mirrors useOutlookStatus.
                const status = err?.response?.status;
                if (status === 401 || status === 404) {
                    return {
                        connected: false,
                        needs_reconnect: false,
                        mail_needs_reconnect: false,
                        email: '',
                    };
                }
                throw err;
            }
        },
        staleTime: 5 * 60 * 1000,
    });
}

/**
 * Gmail emails from the connected Gmail account.
 * Only enabled when Gmail is connected. Polls every 60s.
 */
export function useGmailEmails(gmailConnected) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['gmail', 'emails'],
        queryFn: async () => {
            const data = await apiFetch('/oauth/gmail/emails?max_results=30', getAuthHeader());
            return (data.emails || []).map(e => ({
                ...e,
                _source: 'gmail',
                email_id: `gmail_${e.id}`,
                contact_name: e.from?.replace(/<.*>/, '').trim() || '',
                contact_email: e.from?.match(/<(.+)>/)?.[1] || e.from || '',
                subject: e.subject,
                body: e.snippet,
                status: e.is_unread ? 'unread' : 'read',
                sent_at: e.date,
            }));
        },
        enabled: !!gmailConnected,
        staleTime: 2 * 60 * 1000,
        refetchInterval: 60 * 1000,
    });
}

/**
 * Gmail email detail — fetches the full email body from Gmail API.
 * Used by PersonalThreadView for fetch-on-open when the list data only has
 * a snippet. The Gmail detail endpoint returns the full HTML body.
 *
 * @param {string} emailId - The Gmail message ID (without the 'gmail_' prefix).
 * @param {boolean} enabled - Set to false to disable the query.
 */
export function useGmailEmailDetail(emailId, enabled = true) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['gmail', 'email', emailId],
        queryFn: async () => {
            const data = await apiFetch(`/oauth/gmail/emails/${emailId}`, getAuthHeader());
            return data;
        },
        enabled: enabled && !!emailId,
        staleTime: 5 * 60 * 1000,
    });
}

/**
 * Email templates (personal + admin).
 *
 * @param {boolean} [enabled=true] - Pass `open` from modal surfaces to
 *   keep the fetch lazy; page surfaces use the default (eager).
 */
export function useEmailTemplates(enabled = true) {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    useEffect(() => {
        if (enabled) queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    }, [enabled, queryClient]);
    return useQuery({
        queryKey: ['email-templates'],
        queryFn: () => apiFetch('/email-templates', getAuthHeader()),
        staleTime: 10 * 60 * 1000,
        retry: false,
        select: (data) => ({
            templates: data?.templates || [],
            adminTemplates: data?.admin_templates || [],
        }),
        refetchOnMount: 'always',
        enabled,
    });
}

/**
 * Email signatures (personal + admin).
 *
 * @param {boolean} [enabled=true] - Pass `open` from modal surfaces to
 *   keep the fetch lazy; page surfaces use the default (eager).
 */
export function useSignatures(enabled = true) {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    useEffect(() => {
        if (enabled) queryClient.invalidateQueries({ queryKey: ['signatures'] });
    }, [enabled, queryClient]);
    return useQuery({
        queryKey: ['signatures'],
        queryFn: () => apiFetch('/signatures', getAuthHeader()),
        staleTime: 10 * 60 * 1000,
        retry: false,
        select: (data) => ({
            signatures: data?.signatures || [],
            adminSignatures: data?.admin_templates || [],
            defaultSignature: (data?.signatures || []).find(s => s.is_default) || null,
            // #597 — GET /api/signatures returns this (routes/signatures.py) but the
            // hook used to drop it, so every composer was blind to the org-wide
            // override. AdminSignaturesPage reads it over direct axios; nothing
            // else could. Composers need it to preselect the org's chosen
            // signature (and label it) when one is set.
            activeOverrideId: data?.active_override_id ?? null,
        }),
        refetchOnMount: 'always',
        enabled,
    });
}

// ─── Mutations ───────────────────────────────────────────────────────────────

/**
 * Which endpoint a send should use.
 *
 * Exported and pure so the routing rule can be tested without React Query,
 * axios or a DOM — and so the *fallback* branch has a direct regression test.
 * That fallback is the compatibility guarantee for every caller that has not
 * adopted `provider` yet (issue #484): MeetingActionModal still passes only
 * connection booleans.
 *
 * @param {{provider?: string, gmailConnected?: boolean, outlookConnected?: boolean}} args
 * @returns {'gmail'|'outlook'|'log'}
 */
export function resolveSendRoute({ provider, sourceProvider, gmailConnected, outlookConnected }) {
    // An explicit selection wins, but only if that mailbox is still connected.
    // A token can expire, or the user can disconnect in another tab, between
    // opening the composer and pressing Send. Falling through beats erroring:
    // the message still goes, and the caller is told which mailbox actually
    // sent it via `response.sentVia`.
    if (provider === 'gmail' && gmailConnected) return 'gmail';
    if (provider === 'outlook' && outlookConnected) return 'outlook';

    // No explicit choice → prefer the mailbox the THREAD arrived through, so a
    // reply keeps the correspondence on the address the contact has been
    // writing to (#487). This only ever applies when the user has expressed no
    // preference, so it never overrides a #484 selection.
    if (sourceProvider === 'gmail' && gmailConnected) return 'gmail';
    if (sourceProvider === 'outlook' && outlookConnected) return 'outlook';

    // Neither → the pre-#484 precedence, unchanged.
    if (gmailConnected) return 'gmail';
    if (outlookConnected) return 'outlook';
    return 'log';
}

/**
 * Normalise a recipient list to bare addresses.
 *
 * Accepts either `[{email}]` / `['a@b']` or a comma-joined string.
 * PersonalComposePage has always passed the latter for cc/bcc
 * (`cc.map(c => c.email).join(',')`), which threw `cc.map is not a function`
 * here the moment a CC was present. Accepting both shapes fixes that.
 */
function toAddresses(list) {
    if (!list) return [];
    // splitAddressList, not `split(',')`: a quoted display name
    // (`"Doe, John" <j@x.com>`) contains a comma that is not a separator, and
    // naive splitting turns one recipient into two bogus ones.
    if (typeof list === 'string') {
        return splitAddressList(list).map(extractAddress).filter(Boolean);
    }
    // A bare {email} object that a caller forgot to wrap in an array is a
    // single recipient, not nothing.
    if (!Array.isArray(list)) {
        return list.email ? [extractAddress(list.email)].filter(Boolean) : [];
    }
    return list
        .map(c => (typeof c === 'string' ? c : c?.email))
        // extractAddress, not a bare trim: the personal Outlook sync stores
        // addresses in display form (`Name <a@b>`), and Graph rejects that at
        // send time — so PersonalComposePage / ContactEmailComposer sends stop
        // leaking display forms here.
        .map(a => extractAddress(a))
        .filter(Boolean);
}

/**
 * Send email mutation (via Gmail, Outlook, or log as communication).
 *
 * `provider` ('gmail' | 'outlook') is optional. When supplied and that mailbox
 * is connected it routes the send. When absent — every caller before #484 —
 * the Gmail-first precedence is preserved exactly.
 *
 * The resolved response carries a `sentVia` property naming the route that
 * actually ran, so a caller's confirmation can name what sent rather than what
 * was selected. The two differ when the selected mailbox went away mid-compose.
 */
export function useSendEmail() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    const outlookStatus = useOutlookStatus();

    return useMutation({
        // `allowLogFallback: false` (#659 personal Reply all) refuses the CRM
        // log-only branch below: a caller that promised a delivery must fail
        // loudly rather than write a CRM row that was never sent.
        mutationFn: async ({ to, subject, body: composedBody, cc, bcc, provider, sourceProvider, gmailConnected, outlookConnected, statusUnknown, track = false, contact_id, account_id, attachments, allowLogFallback = true }) => {
            // #970 audit F09 — the signature block's marker and internal id stay
            // in the composer (the picker needs them) but never leave in mail.
            const body = withoutSignatureBlocks(composedBody);
            const ccEmails = toAddresses(cc);
            const bccEmails = toAddresses(bcc);

            // Auto-detect Outlook connection via useOutlookStatus, but allow
            // an explicit outlookConnected override passed in params. Kept
            // Callers that know their status is unresolved pass
            // `statusUnknown` instead of a false boolean (#487).
            const effectiveOutlookConnected =
                typeof outlookConnected === 'boolean'
                    ? outlookConnected
                    : outlookStatus?.data?.connected;

            if (statusUnknown) {
                // #487: refuse rather than fall through to the CRM log. A
                // failed or still-loading status query is NOT "no mailbox" —
                // treating it as such produced a silent log-only row plus a
                // success toast, so the user believed a reply had been
                // delivered when nothing was sent.
                const err = new Error('Mailbox connection status is unavailable');
                err.mailboxStatusUnknown = true;
                throw err;
            }

            const route = resolveSendRoute({
                provider,
                sourceProvider,
                gmailConnected: Boolean(gmailConnected),
                outlookConnected: effectiveOutlookConnected,
            });

            if (route === 'gmail' || route === 'outlook') {
                const res = await axios.post(`${API}/oauth/${route}/send`, {
                    to, subject, body, track,
                    ...(contact_id && { contact_id }),
                    ...(account_id && { account_id }),
                    ...(ccEmails.length > 0 && { cc: ccEmails }),
                    ...(bccEmails.length > 0 && { bcc: bccEmails }),
                    // `attachments` was absent from the destructure above AND
                    // from this payload, so the composer uploaded the file,
                    // handed over its key, and the mail left without it — with
                    // a success toast. Keys only; the bytes are already in S3
                    // and the server re-authorises and re-measures them.
                    ...(attachments?.length && { attachments }),
                }, { headers: getAuthHeader() });
                res.sentVia = route;
                return res;
            }

            if (!allowLogFallback) {
                const err = new Error('No connected mailbox can deliver this message');
                err.noDeliveryRoute = true;
                throw err;
            }

            // #453 — the fallback used to drop contact_id and account_id even
            // though the composer had both, so every logged email was an
            // orphan row: invisible on the contact page, permanently, with no
            // sync to heal it later.
            const res = await axios.post(`${API}/communications`, {
                type: 'email', direction: 'outbound',
                // `toAddresses`, not `[to]`. `to` is a comma-joined STRING from
                // PersonalComposePage and an ARRAY from PersonalThreadView, so
                // `[to]` produced either one element holding every recipient or
                // a nested array — and `CommunicationCreate.to_emails` is
                // `List[str]`, so the nested case was a 422. Same defect class
                // as the `cc.map is not a function` bug on the line above.
                subject, body, to_emails: toAddresses(to),
                ...(contact_id && { contact_id }),
                ...(account_id && { account_id }),
                ...(ccEmails.length > 0 && { cc_emails: ccEmails }),
                ...(bccEmails.length > 0 && { bcc_emails: bccEmails }),
                // Deliberately NOT `attachments`. This branch does not deliver
                // anything — it writes a CRM row — and `CommunicationCreate`
                // has no such field, so posting one would be silently dropped
                // by Pydantic. That is the identical trap the `cc_emails`
                // comment on that model records (#453); adding a second
                // instance of it is worse than the gap.
                //
                // Note the composer refuses to reach this branch with a file
                // anyway: an attachment requires a connected mailbox, and a
                // send with no route is already blocked upstream (#487).
            }, { headers: getAuthHeader() });
            res.sentVia = 'log';
            return res;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inbox', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['gmail', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['outlook', 'emails'] });
            queryClient.invalidateQueries({ queryKey: ['communications'] });
            queryClient.invalidateQueries({ queryKey: ['tracking'] });
        },
        // No mutation-level onError: all callers use mutateAsync with try/catch
        // and show their own contextual toast. A mutation-level onError would
        // fire in addition to the catch block, causing a double toast.
    });
}

/**
 * Tracked emails with open/click/reply stats.
 * @param {number} page - Page number (1-indexed).
 * @param {number} pageSize - Items per page.
 * @param {boolean} enabled - Set to false to disable polling (e.g. when
 *   viewing a unified inbox instead of the personal inbox).
 */
export function useTrackedEmails(page = 1, pageSize = 25, enabled = true) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['tracking', 'emails', page, pageSize],
        queryFn: async () => {
            const data = await apiFetch(`/tracking/emails?page=${page}&page_size=${pageSize}`, getAuthHeader());
            return data;
        },
        enabled,
        staleTime: 30 * 1000,
        refetchInterval: 60 * 1000,
    });
}

/**
 * Single tracked email detail with full event timeline.
 */
export function useTrackedEmailDetail(trackingId) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['tracking', 'email', trackingId],
        queryFn: () => apiFetch(`/tracking/emails/${trackingId}`, getAuthHeader()),
        enabled: !!trackingId,
        staleTime: 15 * 1000,
    });
}

/**
 * Log a communication (call, message, task, reminder).
 */
export function useLogCommunication() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (logForm) =>
            axios.post(`${API}/communications`, logForm, { headers: getAuthHeader() }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['communications'] });
            queryClient.invalidateQueries({ queryKey: ['inbox', 'emails'] });
        },
    });
}

/**
 * Personal inbox thread — fetches all messages in a conversation from the
 * backend thread endpoint (GET /api/inbox/threads/:seed). The backend uses
 * thread_grouping.py to group messages; the frontend just renders the result.
 *
 * @param {string} seed - comm_id, conversation_id, internet_message_id, or
 *   gmail_thread_id that uniquely identifies a message in communications.
 */
export function useInboxThread(seed) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['inbox', 'thread', seed],
        queryFn: () => apiFetch(`/inbox/threads/${seed}`, getAuthHeader()),
        enabled: !!seed,
        staleTime: 30 * 1000,
        // #803 — always re-check on open: within staleTime a cached thread
        // deleted elsewhere (another tab, the provider, the Trash sweep) would
        // otherwise render in full with no fetch to learn it is gone.
        refetchOnMount: 'always',
        // #803 — a 404 is an answer (the thread is gone), not a blip: surface
        // it at once instead of after the app-wide retry, which only prolongs
        // the stale copy a cached bell click shows meanwhile.
        retry: (count, err) => err?.response?.status !== 404 && count < 1,
    });
}

